// Trade identity + Profit-Lock / exit status (UI phase 1, 3 Nov 2026). Display only: every figure comes from the
// backend view-model `identity` (options_hedge/identity.py), built from the trade's own confirmed fills - never from
// the scanner-selected name. Compact form for collapsed rows / cards, detail form for Trade Details.
import React from "react";
import { toast } from "sonner";

const fmtStrike = (v) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtTime = (s) => (s ? new Date(Number(s) * 1000).toLocaleTimeString("en-IN", { hour12: false }) : "—");
const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

export function strikesText(id) {
  const s = id?.strikes || {};
  const ce = (s.CE || []).map((k) => `${fmtStrike(k)} CE`).join(", ");
  const pe = (s.PE || []).map((k) => `${fmtStrike(k)} PE`).join(", ");
  return [ce, pe].filter(Boolean).join(" / ") || "—";
}

const STRATEGY = { PLOCK: "OPT4 Profit-Lock", OPT4: "OPT4", V1: "V1", V2: "V2" };
const PLOCK_TONE = { LOCKED: "text-[#00C805]", "NO SOLUTION": "text-[#FF3B30]", STALE: "text-amber-600", UNVERIFIED: "text-amber-600" };

function copyId(id) {
  try { navigator.clipboard?.writeText(id); toast.success("Trade ID copied"); } catch { /* clipboard unavailable */ }
}

/** One compact line: #id · filled strikes · strategy · Profit-Lock status · counts, plus exit / auto-target warnings. */
export function TradeIdentityLine({ identity: id, testId }) {
  if (!id) return null;
  const strat = id.strategy === "NORMAL" ? `Normal ${id.direction || ""}`.trim() : (STRATEGY[id.strategy] ?? id.strategy);
  const pl = id.plock;
  const adj = pl?.adjustments;
  const r = id.rescue;
  return (
    <div className="text-[10px] leading-tight text-[var(--term-muted)] space-y-0.5" data-testid={testId}>
      <div className="flex flex-wrap gap-x-1.5 items-center">
        <button type="button" className="mono font-semibold text-[var(--term-text)] hover:underline" title={`${id.id} · click to copy`}
          onClick={(e) => { e.stopPropagation(); copyId(id.id); }} data-testid={testId && `${testId}-id`}>#{id.short_id}</button>
        <span title="Original strikes actually filled (not the scanner-selected name)" data-testid={testId && `${testId}-strikes`}>{strikesText(id)}</span>
        {strat && <span className="font-semibold text-[var(--term-text)]">· {strat}</span>}
        {pl && <span className={`font-semibold ${PLOCK_TONE[pl.display] || ""}`} title={pl.why || `${pl.state}${pl.reason ? ` · ${pl.reason}` : ""}`}
          data-testid={testId && `${testId}-plock`}>· {pl.display}{pl.verified !== "VERIFIED" && pl.verified !== "ENDED" ? ` (engine: ${pl.label})` : ""}</span>}
      </div>
      {(r || adj || id.net_open_legs != null) && (
        <div className="flex flex-wrap gap-x-1.5" data-testid={testId && `${testId}-counts`}>
          {adj && <span title={`Adjustments with at least one fill (${adj.partial} partially filled) / configured maximum${adj.lower_bound ? " · older adjustments counted from their legs" : ""}`}>
            {adj.filled} adj ({adj.partial} partial) · cap {adj.cap ?? "—"}</span>}
          {r && <span title="Rescue legs with a confirmed fill / fully closed / still held">· rescue {r.opened} opened · {r.closed} closed · {r.held} held</span>}
          {id.net_open_legs != null && <span title="Distinct contracts whose net confirmed quantity (originals + rescue) is not zero">· {id.net_open_legs} net open legs</span>}
        </div>
      )}
      {id.exit_pause && <div className="text-amber-600 font-semibold" data-testid={testId && `${testId}-paused`}
        title={`Net at LTP ${money(id.exit_pause.net_pnl_ltp)} · bid/ask ${money(id.exit_pause.net_pnl_bidask)} · target ${money(id.exit_pause.target)}`}>
        PAUSED · {id.exit_pause.reason} · checked {fmtTime(id.exit_pause.at)}</div>}
      {id.auto_target && !id.auto_target.usable && <div className="text-amber-600" data-testid={testId && `${testId}-ltp-warning`}
        title="At its last check the profit-target engine could not use one or more legs' LTP (outside the bid/ask or stale): the target cannot fire on it. The LTP P&L shown is indicative.">
        LTP unavailable for auto-target · checked {fmtTime(id.auto_target.at)}</div>}
    </div>
  );
}

/** Detail block (Trade Details / Analyse Trade): full id, scanner name vs filled strikes, charges split, Profit-Lock scan. */
export function TradeIdentityDetail({ identity: id }) {
  if (!id) return null;
  const pl = id.plock;
  const c = id.charges || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs" data-testid="trade-identity-detail">
      <div><span className="text-[var(--term-muted)]">Trade ID </span>
        <button type="button" className="mono hover:underline break-all text-left" onClick={() => copyId(id.id)} title="Copy">{id.id}</button></div>
      <div><span className="text-[var(--term-muted)]">Filled strikes </span>{strikesText(id)}</div>
      <div><span className="text-[var(--term-muted)]">Scanner name </span>{id.name || "—"}</div>
      <div><span className="text-[var(--term-muted)]">Expiry </span>{id.expiry || "—"}</div>
      <div><span className="text-[var(--term-muted)]">Charges paid </span>{money(c.paid_total)}
        <span className="text-[var(--term-muted)]"> (open legs {money(c.paid_open_legs)} · closed legs {money(c.paid_closed_legs)})</span></div>
      {pl && <div className="md:col-span-3"><span className="text-[var(--term-muted)]">Profit-Lock </span>
        engine {pl.label}{pl.reason ? ` (${pl.reason})` : ""} · {pl.verified}{pl.why ? ` - ${pl.why}` : ""} · last scan {fmtTime(pl.scan_at)}
        {pl.scan_min_net != null && <> · scan min {typeof pl.scan_min_net === "number" ? money(pl.scan_min_net) : pl.scan_min_net} / max {typeof pl.scan_max_net === "number" ? money(pl.scan_max_net) : pl.scan_max_net} (after all charges incl. estimated exit)</>}</div>}
    </div>
  );
}
